"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { Loader2, Shield, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/toast";
import { useAuth } from "@/contexts/AuthContext";
import { useEnvironment } from "@/contexts/EnvironmentContext";

export default function LoginPage() {
  const { login, isLoading, isAuthenticated } = useAuth();
  const { completePendingSwitch, setActiveEnvironment } = useEnvironment();
  const { addToast, removeToast } = useToast();
  const router = useRouter();
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showSecret, setShowSecret] = React.useState(false);
  const [formData, setFormData] = React.useState({
    client_id: "",
    client_secret: "",
  });

  // Only redirect away once the session has actually been validated against
  // the backend (isAuthenticated), never on cookie presence alone — see
  // proxy.ts for why that distinction matters. This is also what makes the
  // post-login bootstrap-rotation check work correctly: we deliberately
  // don't navigate to /dashboard right after a successful token exchange —
  // AuthContext leaves isLoading=true until the profile fetch confirms the
  // session. If the bootstrap key needs rotation, that profile request
  // hangs forever (see the Axios 403 interceptor) and RotationGateModal —
  // mounted globally — pops up right here on /login. Only a genuinely
  // confirmed session reaches this effect's redirect.
  React.useEffect(() => {
    if (isLoading || !showSuccess) return;
    if (isAuthenticated) {
      router.replace("/dashboard");
    } else {
      // Login succeeded but the session couldn't be confirmed afterward for
      // a reason other than pending rotation (that case never reaches here).
      setShowSuccess(false);
      addToast(
        "Connexion réussie mais impossible de charger votre session. Réessayez.",
        "error",
      );
    }
  }, [isLoading, isAuthenticated, showSuccess, router, addToast]);

  const handleInputChange =
    (field: keyof typeof formData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.client_id || !formData.client_secret) {
      addToast("Veuillez remplir tous les champs", "error");
      return;
    }

    setIsSubmitting(true);
    const loadingId = addToast("Authentification OAuth2…", "loading");

    try {
      const result = await login({
        client_id: formData.client_id,
        client_secret: formData.client_secret,
        grant_type: "client_credentials",
      });

      removeToast(loadingId);

      if (result.success) {
        // If this login completes a pending environment switch (edge case:
        // user reached /login via the switch dialog cancel → back → login),
        // complete the switch before redirecting.
        if (result.tokenEnvironment) {
          completePendingSwitch();
          // Reconcile the environment context with the JWT's actual claim
          // even for a plain (non-pending-switch) login — a fresh visit
          // always defaults to PRODUCTION, so logging in with a SANDBOX-only
          // key (e.g. any bootstrap key) would otherwise leave `environment`
          // out of sync with which cookie the session actually lives in.
          // See EnvironmentContext.tsx for why that mismatch is dangerous
          // (it makes logout() clear the wrong environment's cookie).
          setActiveEnvironment(result.tokenEnvironment);
        }

        // Don't navigate yet — the redirect effect above does it once the
        // session is actually confirmed (or the rotation gate takes over).
        addToast("Connexion réussie. Vérification de la session…", "success");
        setShowSuccess(true);
      } else {
        addToast(result.error || "Erreur de connexion", "error");
      }
    } catch {
      removeToast(loadingId);
      addToast("Erreur réseau ou serveur", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-muted via-muted to-white relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-emicash-200/30 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-emicash-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-emicash-300/20 rounded-full blur-3xl" />
      </div>

      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA2MCAwIEwgMCAwIDAgNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgwLDAsMCwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-30" />

      <div className="relative w-full max-w-md px-4">
        <div className="backdrop-blur-xl bg-card/80 border border-border/50 rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emicash-500 to-emicash-600 rounded-2xl shadow-lg">
              <span className="text-2xl font-bold text-white">E</span>
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">EMICASH</h1>
            <p className="text-muted-foreground text-sm">Partner Portal</p>
          </div>

          <form onSubmit={handleConnect} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="client_id" className="text-sm font-medium text-foreground">
                Client ID
              </Label>
              <Input
                id="client_id"
                type="text"
                value={formData.client_id}
                onChange={handleInputChange("client_id")}
                placeholder="Votre client ID"
                disabled={isSubmitting || isLoading || showSuccess}
                className="w-full"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="client_secret" className="text-sm font-medium text-foreground">
                Client Secret
              </Label>
              <div className="relative">
                <Input
                  id="client_secret"
                  type={showSecret ? "text" : "password"}
                  value={formData.client_secret}
                  onChange={handleInputChange("client_secret")}
                  placeholder="Votre client secret"
                  disabled={isSubmitting || isLoading || showSuccess}
                  className="w-full pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground disabled:opacity-50"
                  disabled={isSubmitting || isLoading || showSuccess}
                >
                  {showSecret ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-muted/80 p-4">
              <div className="flex gap-3 items-start">
                <Shield className="size-5 text-emicash-600 shrink-0 mt-0.5" aria-hidden />
                <div className="text-left text-sm text-muted-foreground space-y-1">
                  <p className="font-medium text-foreground">OAuth2 — client credentials</p>
                  <p>
                    Utilisez vos clés <code className="text-xs bg-muted px-1 rounded">emi_test_*</code> pour
                    le sandbox, <code className="text-xs bg-muted px-1 rounded">emi_live_*</code> pour la
                    production.
                  </p>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting || isLoading || showSuccess}
              className="w-full cursor-pointer bg-linear-to-r from-emicash-500 to-emicash-600 hover:from-emicash-600 hover:to-emicash-700 text-white font-medium shadow-cta hover:shadow-glow disabled:opacity-50"
            >
              {isSubmitting || isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="size-4 animate-spin" />
                  Connexion…
                </span>
              ) : showSuccess ? (
                "Redirection…"
              ) : (
                "Se connecter au portail"
              )}
            </Button>
          </form>

          <p className="text-center text-xs text-muted-foreground mt-6">
            Accès réservé aux partenaires EMICASH
          </p>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          © {new Date().getFullYear()} EMICASH. Tous droits réservés.
        </p>
      </div>
    </div>
  );
}
